import { CSSProperties, memo, ReactElement, useState } from "react"
import { colors, fonts, glyphs, icons } from "../../Styles"
import { useTranslation } from "react-i18next"
import { ButtonTypes } from "../../Definitions"
import NewButton, { NewButtonList } from "./newButton"

interface Widget_Props {
    width?: string
    height?: string
    header: ReactElement // Add padding: "20px" manually
    content: ReactElement // Add padding: "20px" manually
    editContent?: ReactElement // Add padding: "20px" manually
    contentScroll?: boolean
    bgColor?: string
    bannerContent?: string
    onSave?:() => void
    children?: React.ReactNode
}
/**
 * Widget
 * @param width overrides the default width (fit-content).
 * @param height overrides the default height (fit-content).
 * @param header elements for header-area goes here.
 * @param content elements for content-area goes here.
 * @param editContent elements for content-area in editmode goes here.
 * @param bgColor overrides the default backgroundcolor (greyLightTertiary).
 * @param bannerContent elements for banner-area goes here.
 * @param onSave functionality on "save btn". 
 * @returns a basic frame for widgets
 */
export default memo(function CoreWidget({ width, height, header, content, editContent, bgColor, bannerContent, onSave, contentScroll }: Widget_Props) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)

  const handleSaveClick = () => {
    setIsEditing(!isEditing)
    if (onSave) onSave()
  }

  const handleCancelClick = () => {
    setIsEditing(!isEditing)
  }

  const handleEditClick = () => {
    setIsEditing(!isEditing)
  }

  const _width = width ? width : "fit-content"

  const _height = height ? height : "fit-content"

  const _bgColor = bgColor ? bgColor : colors.greyLightTertiary

  const _overflowScroll = contentScroll ? "scroll" : "hidden"

  const _bannerContent =
    <div style={{ height: "30px", width: "100%", backgroundColor: colors.greyLightSecondary, display: "flex", alignItems: "center", justifyContent: "start" }}>
      <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary, margin: "0px 20px" }}>{bannerContent}</p>
    </div>

  return (
    <div style={{ display: "flex", height: _height, width: _width, flexDirection: "column" }}>
      <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
        {editContent ?
          <div style={{ margin: "0px 20px 0px 0px" }}>
            {!isEditing
              ? <NewButton content={t("buttons.edit", { defaultValue: "edit" })} startEnhancer={glyphs.Edit({ fillColor: colors.greyLightTertiary })} buttonType={ButtonTypes.tableButton} onClick={handleEditClick} />
              : <NewButtonList
                contents={[t("buttons.save", { defaultValue: "save" }), t("buttons.cancel", { defaultValue: "cancel" }), t("buttons.edit", { defaultValue: "edit" })]}
                onClick={(i => {
                  if (i === 0) { handleSaveClick() }
                  else if (i === 1) { handleCancelClick() }
                })}
                buttonType={ButtonTypes.tableButton}
                disabled={[false, false, true]}
                overrides={(index) => ({
                  baseStyles: {
                    backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary
                  },
                  disabledStyles: {
                    opacity: index === 2 ? 1 : 0.5
                  }
                })}
                startEnhancers={[glyphs.Check({ fillColor: colors.greyLightTertiary }), glyphs.Close({ fillColor: colors.greyLightTertiary }), glyphs.Edit({ fillColor: colors.greyLightTertiary })]}
              />
            }
          </div>
          : <></>}
      </div>
      <div style={{ borderRadius: "10px", border: "5px solid " + colors.greyPrimary, height: _height }}>
        <div style={{ borderRadius: "5px 5px 0px 0px", height: "50px", width: "auto", backgroundColor: colors.greyPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {header}
        </div>
        {bannerContent
          ? _bannerContent
          : <></>
        }
        <div style={{ overflowY: _overflowScroll, scrollbarWidth: "thin", scrollbarColor: colors.greyLightPrimary + colors.transparent, borderRadius: "0px 0px 5px 5px", height: bannerContent ? "calc(100% - 80px)" : "calc(100% - 50px)", width: "auto", backgroundColor: _bgColor, display: "flex", justifyContent: "center" }}>
          {isEditing ? (
            <>{editContent}</>
          ) : (
            <>{content}</>
          )}
        </div>
      </div>
    </div>
  )
});